'use strict';

class ApiRequestError extends Error {
  constructor(status, code, message, fields) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.fields = fields || {};
  }
}

class ApiClient {
  constructor(apiPrefix = '/api') {
    this.apiPrefix = String(apiPrefix || '/api').replace(/\/$/, '');
  }

  async request(path, options) {
    const settings = options || {};
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      const requestPath = path.startsWith('/api/')
        ? `${this.apiPrefix}${path.slice(4)}`
        : path;
      const response = await fetch(requestPath, {
        method: settings.method || 'GET',
        credentials: 'same-origin',
        headers: settings.body ? { 'Content-Type': 'application/json' } : {},
        body: settings.body ? JSON.stringify(settings.body) : undefined,
        signal: controller.signal
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = payload.error || {};
        throw new ApiRequestError(
          response.status,
          error.code || 'REQUEST_FAILED',
          error.message || '请求失败，请稍后重试',
          error.fields
        );
      }
      return payload;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new ApiRequestError(0, 'NETWORK_TIMEOUT', '连接超时，请检查网络');
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  session() { return this.request('/api/auth/session'); }
  register(body) { return this.request('/api/auth/register', { method: 'POST', body }); }
  login(body) { return this.request('/api/auth/login', { method: 'POST', body }); }
  logout() { return this.request('/api/auth/logout', { method: 'POST', body: {} }); }
  progress() { return this.request('/api/player/progress'); }
  currentConfig() { return this.request('/api/game-config/current'); }
  startRun(body) { return this.request('/api/runs/start', { method: 'POST', body }); }
  finishRun(runId, body) {
    return this.request(`/api/runs/${encodeURIComponent(runId)}/finish`, { method: 'POST', body });
  }
  abandonRun(runId) {
    return this.request(`/api/runs/${encodeURIComponent(runId)}/abandon`, { method: 'POST', body: {} });
  }
  dashboard() { return this.request('/api/player/dashboard'); }
  leaderboard(type, period, limit) {
    const params = new URLSearchParams({ type: type || 'DAILY', limit: String(limit || 100) });
    if (period) params.set('period', period);
    return this.request(`/api/leaderboards?${params.toString()}`);
  }
  cosmetics() { return this.request('/api/player/cosmetics'); }
  selectCosmetic(body) {
    return this.request('/api/player/cosmetics/selection', { method: 'PUT', body });
  }
}

module.exports = { ApiClient, ApiRequestError };
